import React, { useMemo } from 'react';
import { MetricCard, SpinnerDotted } from '../../atoms';
import { AttentionAlert, UserAlert } from '../../atoms/alerts';
import { LodingSquareTabbar, PreferredPartner } from '../../molecules';
import { RatingCard, CommentSection, DoughnutGraph } from '../../organisms';
import { commaingNumber } from '../../../../utils';
import WalletUtilizationCommon from './WalletUtilizationCommon';
import { UtilizationSankyChart } from '../../../tailwind/organisms';
import { Tooltip } from 'antd';
import { CommonSplitPlot } from '../../../opdClaims';
import { OverviewSectionIcon, utilizationBenefitTypeToOverviewIconKey } from '../../../overview/OverviewSectionIcon';

const Benefit = ({ metricObj = {}, ratingObj = {}, utilizationObj = {}, costSavingObj = {}, sqaureTabHandler, loading, selectedContractObj, benefitType, benefitName, walletUtilizationObj = {}, showNoUtilization}) => {
  const {
    healthCheckAvailedPercentage, successMetric, donutGraphData = [], partners = [], activeEmployeesAppointmentsCount, inactiveEmployeesAppointmentsCount,
    activeDependentsAppointmentsCount, inactiveDependentsAppointmentsCount, totalHealthChecksCompleted, unique_users, repeat_users, activeCustomerCount,
    activeEmployeesUpcomingAppointmentsCount, activeDependentsUpcomingAppointmentsCount, totalAmountSpent,
    activeUniqueEmployeesAppointmentsCount = 0, inactiveUniqueEmployeesAppointmentsCount = 0, hasCustomerFormSubmissions,
    activeUniqueDependentsAppointmentsCount = 0, inactiveUniqueDependentsAppointmentsCount = 0
  } = metricObj;

  const { ratingsPercent, totalratings, averagerating, commentSectionData = [] } = ratingObj;
  const { barlabels = [], bardata = [], barBgColors = [], xAxesLabel } = utilizationObj;
  const { appointment_count, cost, savings, self_savings } = costSavingObj;
  const currentYear = selectedContractObj?.is_running_contract
  const benefitLineIconKey = utilizationBenefitTypeToOverviewIconKey(benefitType)

  const tooltipText = (selfCount = 0, dependentCount = 0) => {
    return (
      <div>
        {employeeData('Self appointments', selfCount)}
        {employeeData('Dependents appointments', dependentCount)}
        <hr className='my-3' />
        {employeeData('Total appointments', selfCount + dependentCount)}
      </div>
    )
  }

  const employeeData = (text, count) => {
    return (
      <div className='flex justify-between'>
        <span className='font-semibold'>{text}</span>
        <span className='font-semibold'>{commaingNumber(count)}</span>
      </div>
    )
  }

  const metricValueSuffix = (selfCount, dependentCount, benefitName='appointments') => {
    return (
      <span >
        <span className='font-semibold text-gray-800'>
        Upcoming {benefitName}
        </span>
        <Tooltip placement='bottom' title={tooltipText(selfCount, dependentCount)} overlayStyle={{width: '16rem'}}>
          {/* <img src={info_icon} className='h-3 w-3 ml-1 inline cursor-pointer' alt='info_icon' /> */}
        </Tooltip>
      </span>
    )
  }

  const sankyChartData = useMemo(() => {
    const employeesAppointmentsCount = (activeEmployeesAppointmentsCount || 0) + (activeDependentsAppointmentsCount || 0)
    const dependentsAppointmentsCount = (inactiveEmployeesAppointmentsCount || 0) + (inactiveDependentsAppointmentsCount || 0)

    const totalUnique = (activeUniqueEmployeesAppointmentsCount || 0) + (inactiveUniqueEmployeesAppointmentsCount || 0) +
      (activeUniqueDependentsAppointmentsCount || 0) + (inactiveUniqueDependentsAppointmentsCount || 0)
    const uniqueActiveBranch = (activeUniqueEmployeesAppointmentsCount || 0) + (activeUniqueDependentsAppointmentsCount || 0)
    const uniqueInactiveBranch = (inactiveUniqueEmployeesAppointmentsCount || 0) + (inactiveUniqueDependentsAppointmentsCount || 0)

    const logValue = (v) => {
      const scaled = Math.log((v || 1) + 1)
      return Math.max(0.8, scaled)
    }
    const v24 = logValue(activeUniqueEmployeesAppointmentsCount)
    const v25 = logValue(activeUniqueDependentsAppointmentsCount)
    const v36 = logValue(inactiveUniqueEmployeesAppointmentsCount)
    const v37 = logValue(inactiveUniqueDependentsAppointmentsCount)

    const LINE_BREAK = '__BR__'
    // First node: total users + completed appointments
    const point1 = `${commaingNumber(totalUnique)} Users${LINE_BREAK}(Completed ${commaingNumber(totalHealthChecksCompleted || 0)} appointments)`
    // Second node: Active / Inactive users + completed appointments
    const point2 = `${commaingNumber(uniqueActiveBranch)} Active Users${LINE_BREAK}(Completed ${commaingNumber(employeesAppointmentsCount || 0)} appointments)`
    const point3 = `${commaingNumber(uniqueInactiveBranch)} Inactive Users${LINE_BREAK}(Completed ${commaingNumber(dependentsAppointmentsCount || 0)} appointments)`
    // Third node under Active: Employees / Dependents
    const point4 = `${commaingNumber(activeUniqueEmployeesAppointmentsCount || 0)} Employees${LINE_BREAK}(Completed ${commaingNumber(activeEmployeesAppointmentsCount || 0)} appointments)`
    const point5 = `${commaingNumber(activeUniqueDependentsAppointmentsCount || 0)} Dependents${LINE_BREAK}(Completed ${commaingNumber(activeDependentsAppointmentsCount || 0)} appointments)`
    // Third node under Inactive: Employees / Dependents (added space to force uniqueness)
    const point6 = `${commaingNumber(inactiveUniqueEmployeesAppointmentsCount || 0)} Employees ${LINE_BREAK}  (Completed ${commaingNumber(inactiveEmployeesAppointmentsCount || 0)} appointments)`
    const point7 = `${commaingNumber(inactiveUniqueDependentsAppointmentsCount || 0)} Dependents ${LINE_BREAK}(Completed ${commaingNumber(inactiveDependentsAppointmentsCount || 0)} appointments)`

    const data = {
      nodes: [{ id: point1 }, { id: point2 },
              { id: point3 }, { id: point4 },
              { id: point5 }, { id: point6 },
              { id: point7 }],
      links: [
        { source: point1, target: point2, value: v24 + v25 },
        { source: point1, target: point3, value: v36 + v37 },
        { source: point2, target: point4, value: v24 },
        { source: point2, target: point5, value: v25 },
        { source: point3, target: point6, value: v36 },
        { source: point3, target: point7, value: v37 }
      ]
    }

    const colors = ['#1D4ED8', '#14866D', '#E8432C', '#4FAD96', '#60A5FA', '#475569', '#9CA3AF']

    return { data, colors }
  }, [
    totalHealthChecksCompleted,
    activeEmployeesAppointmentsCount, inactiveEmployeesAppointmentsCount, activeDependentsAppointmentsCount, inactiveDependentsAppointmentsCount,
    activeUniqueEmployeesAppointmentsCount, inactiveUniqueEmployeesAppointmentsCount,
    activeUniqueDependentsAppointmentsCount, inactiveUniqueDependentsAppointmentsCount
  ])

  const formattedDistributionData = useMemo(() => {
    const formatted = {};
    barlabels.forEach((label, index) => {
      const val = bardata[index];
      const volume = typeof val === 'object' ? val.t : val;
      const key = xAxesLabel === 'Age' ? `${label} age group` : label;
      formatted[key] = {
        volume: volume,
        amount: 0 
      };
    });
    return formatted;
  }, [barlabels, bardata, xAxesLabel]);


  return (
    <div>
      {(!loading && currentYear && healthCheckAvailedPercentage < successMetric) &&
        <AttentionAlert
          title='Attention Required'
          description={
            <span>
              Utilization for your organization is low as per the success metrics set for your organization. To improve
              the utilization please reach out to your{' '}
              <span className='text-blue-500 font-semibold'>
                customer success manager.
              </span>
            </span>
          }
          classes='mt-6'
        />}
      <p className='mt-8 text-xs font-semibold uppercase tracking-wider text-gray-500'>Sponsored {benefitName} summary</p>
      { !loading ?
        <>

          <div className='mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3'>
            <MetricCard
              icon={<OverviewSectionIcon name='wallet' />}
              loading={loading}
              loaderHeight={178}
              title={`Total amount spent on sponsored ${benefitName}`}
              titleClasses='text-sm font-semibold text-gray-900'
              subTitle='Calculated based on completed appointments only'
              subTitleClasses='mt-1 text-xs leading-relaxed text-gray-600'
              metricValue={<span className='font-jetbrains-mono text-3xl font-semibold tabular-nums text-gray-900'>₹{commaingNumber(totalAmountSpent)}</span>}
              metricClasses='mt-5 flex flex-col items-start gap-2 text-gray-700'
              classes='min-h-[178px] rounded-lg border-gray-300 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-gray-400 hover:shadow-md'
            />
            { showNoUtilization == false && (<MetricCard
              icon={<OverviewSectionIcon name={benefitLineIconKey} />}
              loading={loading}
              loaderHeight={178}
              title={`Completed ${benefitName}`}
              titleClasses='text-sm font-semibold text-gray-900'
              subTitle='Completed (unique) appointments for self and dependents'
              subTitleClasses='mt-1 text-xs leading-relaxed text-gray-600'
              metricValue={<span className='font-jetbrains-mono text-3xl font-semibold tabular-nums text-gray-900'>{commaingNumber(totalHealthChecksCompleted)}</span>}
              metricValueSuffix={<span className='font-sans text-xs text-gray-700'>appointments</span>}
              metricClasses='mt-5 flex flex-col items-start gap-2 text-gray-700'
              classes='min-h-[178px] rounded-lg border-gray-300 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-gray-400 hover:shadow-md'
            />)}
            { showNoUtilization == false && (<MetricCard
              icon={<OverviewSectionIcon name='upcoming_appointments' />}
              loading={loading}
              loaderHeight={178}
              title={metricValueSuffix(activeEmployeesUpcomingAppointmentsCount, activeDependentsUpcomingAppointmentsCount, benefitName)}
              titleClasses='text-sm font-semibold text-gray-900'
              subTitle='Upcoming (unique) appointments for self and dependents'
              subTitleClasses='mt-1 text-xs leading-relaxed text-gray-600'
              metricValue={<span className='font-jetbrains-mono text-3xl font-semibold tabular-nums text-gray-900'>{commaingNumber(activeEmployeesUpcomingAppointmentsCount + activeDependentsUpcomingAppointmentsCount)}</span>}
              metricValueSuffix={<span className='font-sans text-xs text-gray-700'>appointments</span>}
              metricClasses='mt-5 flex flex-col items-start gap-2 text-gray-700'
              classes='min-h-[178px] rounded-lg border-gray-300 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-gray-400 hover:shadow-md'
            />)}
          </div>

          { showNoUtilization == false && (<div><p className='mt-10 text-base font-semibold text-gray-900'>User-wise appointment breakdown</p>

        <UtilizationSankyChart sankeyData={sankyChartData} classes='mt-4 rounded-xl border border-gray-200 bg-white px-6 py-10 shadow-sm md:px-9 md:py-14' height={450} /></div>)}
        </> :
        <div className='h-40 flex items-center justify-center'><SpinnerDotted /></div>
      }

      <WalletUtilizationCommon metricObj={metricObj}  loading={loading} benefitName={benefitName} walletUtilizationObj={walletUtilizationObj} uniqueUsers={unique_users} activeCustomerCount={activeCustomerCount}/>
    { showNoUtilization == false && (<div>
      <div className='mt-12 flex flex-col gap-8 lg:flex-row lg:justify-between'>
        <div className='flex min-w-0 flex-1 flex-col lg:mr-4 lg:w-1/2'>
          <p className='mb-1 text-xs font-semibold uppercase tracking-wider text-gray-500'>{`${benefitName} completion modes`}</p>
          <div className='mb-3 text-sm text-gray-600'>
            Distribution of completed {benefitName} appointments by mode
          </div>
          <DoughnutGraph
            loading={loading}
            loaderHeight={200}
            title={benefitName}
            graphData={donutGraphData}
            classes='h-full'
            heightOfLoader={200}
            id={benefitType}
            hideBrackets={true}
          />
        </div>
        <div className='flex min-w-0 flex-1 flex-col lg:w-1/2'>
          <p className='mb-1 text-xs font-semibold uppercase tracking-wider text-gray-500'>
            Top preferred providers
          </p>
          <div className='mb-3 text-sm text-gray-600'>
            Ranks partner providers based on no. of employees who selected them for completing their appointments
          </div>
          <PreferredPartner loading={loading} loaderHeight={200} list={partners} header='Partners' classes='h-full' />
        </div>
      </div>
      {hasCustomerFormSubmissions && <p className='text-sm text-gray-600 mt-2 mb-0 italic leading-none'> *Also includes data from the form submitted by the customer</p>}

      {/* <div>
        {benefitArrForSponsoredAndMoneySpent.includes(benefitType) &&(
        <div>
          <p className='text-gray-500 text-base font-semibold mt-12 mb-3 leading-none'>
            Spent and savings on {benefitName?.toLowerCase()}
          </p>
            <div className='flex flex-row'>
              <MetricCard
                loading={loading}
                loaderHeight={120}
                svgsrc={totalamont}
                titleClasses='block'
                title={`Total amount spent on sponsored ${benefitName?.toLowerCase()}`}
                metricValue={<span className='font-semibold text-3xl text-gray-700 mt-3'>₹{commaingNumber(cost)}</span>}
                classes='w-1/3 inline-block mr-5'
              />
              <MetricCard
                loading={loading}
                loaderHeight={120}
                svgsrc={reduceCost}
                titleClasses='block'
                title={`Amount saved by you on sponsored ${benefitName?.toLowerCase()}`}
                metricValue={<span className='font-semibold text-3xl text-gray-700'>₹{commaingNumber(savings)}</span>}
                classes='w-1/3 inline-block mr-5'
              />
              <MetricCard
                loading={loading}
                loaderHeight={120}
                svgsrc={reduceCost}
                titleClasses='block'
                title={
                  <span>
                    Amount saved by you on self paid {benefitName?.toLowerCase()}<span className='font-semibold text-lg leading-3'>*</span>
                  </span>
                }
                metricValue={<span className='font-semibold text-3xl text-gray-700'>₹{commaingNumber(self_savings)}</span>}
                classes='w-1/3 inline-block'
              />
            </div>
          <p className='text-sm text-gray-600 mt-2 mb-0 italic leading-none'>
            <span className='font-semibold'>*{appointment_count}</span> additional tests/packages were availed by
              employees/dependents on self paid basis
          </p>
        </div>)}
      </div>  */}

      {(totalratings > 0 || commentSectionData?.length > 0) && <><div className='mt-12 flex flex-col gap-8 lg:flex-row'>
        <div className='flex flex-col pr-4 lg:w-2/4'>
          <div className='mb-1 text-xs font-semibold uppercase tracking-wider text-gray-500'>Ratings and reviews</div>
          <div className='mb-3 text-sm text-gray-600'>
            These ratings are captured after completing the {benefitName?.toLowerCase()}
          </div>
          <RatingCard
            loading={loading}
            loaderHeight={200}
            rating={averagerating}
            number_of_ratings={totalratings}
            five_star_rating={ratingsPercent && ratingsPercent[4]}
            four_star_rating={ratingsPercent && ratingsPercent[3]}
            three_star_rating={ratingsPercent && ratingsPercent[2]}
            two_star_rating={ratingsPercent && ratingsPercent[1]}
            one_star_rating={ratingsPercent && ratingsPercent[0]}
            classes='h-full'
          />
        </div>

        <div className='flex w-full flex-col lg:w-2/4'>
          <div className='mb-1 text-xs font-semibold uppercase tracking-wider text-gray-500'>Top comments</div>
          <div className='mb-3 text-sm text-gray-600'>
            These reviews are captured after completing the {benefitName?.toLowerCase()}
          </div>
          <CommentSection loading={loading} loaderHeight={200} points={commentSectionData} classes='h-full' />
        </div>
      </div>
      {hasCustomerFormSubmissions && <p className='text-sm text-gray-600 mt-2 mb-0 italic leading-none'> *Also includes data from the form submitted by the customer</p>}
      </>
      }

      <div className='mt-12'>
        <div className='text-xs font-semibold uppercase tracking-wider text-gray-500'>Employee profile distribution</div>
          <div className='my-2 text-sm text-gray-600'>
            Share of employees (who completed at least one appointment) by department, location, age group, and gender
          </div>
        <LodingSquareTabbar
          loading={loading}
          loaderHeight={25}
          list={['Departments', 'Location', 'Age', 'Gender']}
          handler={sqaureTabHandler}
          classes='mt-3'
        />
        <div className='mt-5'>
          {bardata && bardata.length === 0 ? (
            <div className='rounded-xl border border-gray-200 bg-white p-6 shadow-sm'>
              <UserAlert
                para={`${xAxesLabel} data is not available`}
                paraClasses=' font-semibold text-gray-600'
                imgClasses=' h-5 w-5'
                classes='p-4'
              />
            </div>
          ) : (
            <CommonSplitPlot
              showDownload
              data={formattedDistributionData}
              title={xAxesLabel}
              loading={loading}
              initialMetric='volume'
              hideToggle={true}
              hideSummary={true}
              hideAov={true}
              hideImage={true}
              precision={2}
              shareLabel='%Share'
              hideBrackets={true}
            />
          )}
        </div>
      </div>
      </div>)}
    </div>
  );
};

export default Benefit;
